import React, {useEffect, useState} from "react";
import {useNavigate, useLocation} from "react-router-dom";
import axios from "axios";
import {
    PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import {
    Users, TrendingUp, Clock, AlertTriangle, CheckCircle, DollarSign, ChevronRight, Search, ChevronLeft, Trash2
} from 'lucide-react';
import BackOfficeMenu from "../components/BackOfficeMenu";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import {usePermissions} from "../../utils/usePermissions";
import { ProtectedFinancialCard } from "../../components/ProtectedSensitiveData";
import "./DashboardClient.css";

const API_URL = import.meta.env.VITE_API_URL;

const COLORS = {
    primaryBlue: '#1e3a5f',
    primaryYellow: '#ffd700',
    accentOrange: '#ff8c00',
    green: '#00C49F',
    red: '#ef4444'
};

const CHART_COLORS = [COLORS.primaryBlue, COLORS.primaryYellow, COLORS.accentOrange, COLORS.green];

const DashboardClient = ({onNavigate, onLogout, currentPage}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const {hasPermission, typeUser} = usePermissions();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeDfPeriod, setActiveDfPeriod] = useState('total'); // 'today', 'week', 'month', 'year', 'total'
    const [revenueFilters, setRevenueFilters] = useState({
        mois: '',
        annee: '',
        dateDebut: '',
        dateFin: ''
    });

    const getInitialTab = () => {
        const params = new URLSearchParams(location.search);
        const tab = params.get('tab');
        if (tab && ['retards', 'bloques', 'echeances', 'supprimes', 'clientsSupprimes', 'dossiersSupprimes', 'extensions'].includes(tab)) {
            return tab;
        }
        return 'retards';
    };

    // États pour les tableaux de gestion
    const [activeTab, setActiveTab] = useState(getInitialTab);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tab = params.get('tab');
        if (tab && ['retards', 'bloques', 'echeances', 'supprimes', 'clientsSupprimes', 'dossiersSupprimes', 'extensions'].includes(tab)) {
            setActiveTab(tab);
        }
    }, [location.search]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPageByTab, setCurrentPageByTab] = useState({
        retards: 1,
        bloques: 1,
        echeances: 1,
        supprimes: 1,
        clientsSupprimes: 1,
        dossiersSupprimes: 1,
        extensions: 1
    });
    const itemsPerPage = 8;

    useEffect(() => {
        if (!typeUser) return;
        if (typeUser !== "admin" && typeUser !== "moderateur") {
            onNavigate("profile");
            return;
        }
        if (typeUser === "moderateur" && !hasPermission("dashboard", "read")) {
            onNavigate("profile");
            return;
        }
    }, [onNavigate, typeUser, hasPermission]);

    useEffect(() => {
        fetchClientStats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [revenueFilters.mois, revenueFilters.annee, revenueFilters.dateDebut, revenueFilters.dateFin]);

    const fetchClientStats = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const params = new URLSearchParams();
            if (revenueFilters.dateDebut && revenueFilters.dateFin) {
                params.append('dateDebut', revenueFilters.dateDebut);
                params.append('dateFin', revenueFilters.dateFin);
            } else {
                if (revenueFilters.mois) params.append('mois', revenueFilters.mois);
                if (revenueFilters.annee) params.append('annee', revenueFilters.annee);
            }

            const response = await axios.get(`${API_URL}/dashboard/client-stats?${params.toString()}`, {
                headers: {Authorization: `Bearer ${token}`}
            });
            if (response.data.success) {
                setStats(response.data.data);
            }
        } catch (error) {
            console.error('Erreur chargement statistiques clients:', error);
        } finally {
            setLoading(false);
        }
    };

    // Animations anime.js
    useEffect(() => {
        if (loading || !stats || typeof window.anime !== 'function') return;

        setTimeout(() => {
            window.anime({
                targets: '.dbc-header',
                opacity: [0, 1],
                translateY: [-30, 0],
                duration: 800,
                easing: 'easeOutExpo'
            });

            window.anime({
                targets: '.dbc-stat-card',
                opacity: [0, 1],
                translateY: [20, 0],
                duration: 600,
                easing: 'easeOutBack',
                delay: window.anime.stagger(100, {start: 200})
            });

            window.anime({
                targets: '.dbc-chart-card',
                opacity: [0, 1],
                scale: [0.95, 1],
                duration: 800,
                easing: 'easeOutQuad',
                delay: window.anime.stagger(150, {start: 500})
            });

            window.anime({
                targets: '.dbc-table-section',
                opacity: [0, 1],
                translateY: [40, 0],
                duration: 800,
                easing: 'easeOutQuad',
                delay: 800
            });
        }, 100);
    }, [loading, stats]);

    // Reset pagination when tab or search changes
    useEffect(() => {
        setCurrentPageByTab(prev => ({
            ...prev,
            [activeTab]: 1
        }));
    }, [activeTab, searchTerm]);

    const formatCurrency = (val) => {
        return new Intl.NumberFormat('fr-TN', {
            style: 'currency',
            currency: 'TND',
            minimumFractionDigits: 3,
            maximumFractionDigits: 3
        }).format(val);
    };

    const goToProspectDetail = (prospectId) => {
        if (!prospectId) return;
        navigate(`/prospects/${prospectId}`);
    };

    // Helper pour filtrer et paginer les données
    const getPaginatedData = (data = [], page = 1) => {
        const filtered = data.filter(item => {
            const searchStr = searchTerm.toLowerCase();
            return (
                (item.clientName?.toLowerCase().includes(searchStr)) ||
                (item.referenceSteg?.toLowerCase().includes(searchStr)) ||
                (item.supprimePar?.toLowerCase().includes(searchStr)) ||
                (item.cin?.toLowerCase().includes(searchStr)) ||
                (item.telephone?.toLowerCase().includes(searchStr)) ||
                (item.typeClient?.toLowerCase().includes(searchStr)) ||
                (item.statutSuppression?.toLowerCase().includes(searchStr)) ||
                (String(item.montant).includes(searchStr))
            );
        });

        const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
        const startIndex = (page - 1) * itemsPerPage;
        const paginated = filtered.slice(startIndex, startIndex + itemsPerPage);

        return {
            data: paginated,
            totalPages,
            totalItems: filtered.length
        };
    };

    if (loading) {
        return (
            <div className="dbc-container">
                <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />
                <div className="dbc-content">
                    <LoadingSpinner message="Chargement des données financières..." />
                </div>
            </div>
        );
    }

    if (!stats) {
        return (
            <div className="dbc-container">
                <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />
                <div className="dbc-content">
                    <div className="error-message">Impossible de charger les statistiques</div>
                </div>
            </div>
        );
    }

    const {revenue, clients, echeances, paiementsSupprimes, repartitionMethodesPaiement, elementsSupprimes, alertes, dfStats} = stats;
    const retardsList = Array.isArray(echeances?.retards) ? echeances.retards : [];
    const echeancesList = Array.isArray(echeances?.details) ? echeances.details : [];
    const paiementsSupprimesList = Array.isArray(paiementsSupprimes?.details) ? paiementsSupprimes.details : [];
    const methodesEncaissees = Array.isArray(repartitionMethodesPaiement?.encaisses) ? repartitionMethodesPaiement.encaisses : [];
    const methodesAVenir = Array.isArray(repartitionMethodesPaiement?.aVenir) ? repartitionMethodesPaiement.aVenir : [];
    const clientsSupprimesList = Array.isArray(elementsSupprimes?.clients?.details) ? elementsSupprimes.clients.details : [];
    const dossiersSupprimesList = Array.isArray(elementsSupprimes?.dossiers?.details) ? elementsSupprimes.dossiers.details : [];
    const sastraBloquesList = Array.isArray(alertes?.sastraBloques) ? alertes.sastraBloques : [];
    const extensionsList = Array.isArray(alertes?.extensionsEnAttente) ? alertes.extensionsEnAttente : [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 2, 0, 23, 59, 59, 999);
    const filterStartLabel = today.toLocaleDateString('fr-FR');
    const filterEndLabel = endOfNextMonth.toLocaleDateString('fr-FR');

    const computedRetardsList = echeancesList.filter(e => {
        const d = e.dateEcheance ? new Date(e.dateEcheance) : null;
        return d && d < today;
    });

    const echeancesAVenirList = echeancesList.filter(e => {
        const d = e.dateEcheance ? new Date(e.dateEcheance) : null;
        return d && d >= today && d <= endOfNextMonth;
    });

    const safeRetardsList = retardsList.length ? retardsList : computedRetardsList;

    const formatDate = (date) => date ? new Date(date).toLocaleDateString('fr-FR') : '-';

    const renderPaymentMethodRows = (items, emptyLabel, showNextDate = false) => {
        if (!items.length) {
            return <div className="dbc-payment-method-empty">{emptyLabel}</div>;
        }

        return items.map((item) => (
            <div key={`${item.method}-${item.montant}-${item.count}`} className="dbc-payment-method-row">
                <div className="dbc-payment-method-main">
                    <span className="dbc-payment-method-name">{item.label || item.method || 'Autre'}</span>
                    <span className="dbc-payment-method-count">
                        {item.count || 0} {showNextDate ? 'échéance(s)' : 'paiement(s)'}
                    </span>
                </div>
                <div className="dbc-payment-method-values">
                    <span className="dbc-payment-method-amount">{formatCurrency(item.montant || 0)}</span>
                    {showNextDate ? (
                        <span className="dbc-payment-method-next">Prochaine: {formatDate(item.prochaineEcheance)}</span>
                    ) : (
                        <span className="dbc-payment-method-percent">{Number(item.pourcentage || 0).toFixed(1)}%</span>
                    )}
                </div>
                <div className="dbc-payment-method-bar">
                    <span style={{width: `${Math.min(100, Math.max(0, item.pourcentage || 0))}%`}} />
                </div>
            </div>
        ));
    };

    const dataByTab = {
        retards: safeRetardsList,
        bloques: sastraBloquesList,
        echeances: echeancesAVenirList,
        supprimes: paiementsSupprimesList,
        clientsSupprimes: clientsSupprimesList,
        dossiersSupprimes: dossiersSupprimesList,
        extensions: extensionsList
    };

    const activeList = dataByTab[activeTab] || [];
    const activePage = currentPageByTab[activeTab] || 1;
    const activePagination = getPaginatedData(activeList, activePage);

    return (
        <div className="dbc-container">
            <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />

            <div className="dbc-content">
                <div className="dbc-header">
                    <div>
                        <h1>Dashboard Clients & Finances</h1>
                        <p>Suivi en temps réel des revenus et du portefeuille</p>
                    </div>
                    <button className="dbc-refresh-btn" onClick={fetchClientStats}>
                        Actualiser
                    </button>
                </div>

                <ProtectedFinancialCard title="Suivi des Revenus protégé" lockedClassName="sdl-unlock-section-card">
                <div className="dbc-section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px' }}>
                    <h2 className="dbc-section-title" style={{ margin: 0 }}>Suivi des Revenus</h2>
                    <div className="dbc-filters" style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <select
                            className="dbc-filter-select"
                            style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', outline: 'none', background: 'white' }}
                            value={revenueFilters.annee}
                            onChange={e => {
                                const yVal = e.target.value;
                                setRevenueFilters(prev => ({
                                    ...prev,
                                    annee: yVal,
                                    mois: !yVal ? '' : prev.mois,
                                    dateDebut: '',
                                    dateFin: ''
                                }));
                            }}
                        >
                            <option value="">Toutes les années</option>
                            {['2020', '2021', '2022', '2023', '2024', '2025', '2026', '2027', '2028', '2029', '2030'].map(y => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                        <select
                            className="dbc-filter-select"
                            style={{
                                padding: '6px 12px',
                                borderRadius: '6px',
                                border: '1px solid #e2e8f0',
                                outline: 'none',
                                background: !revenueFilters.annee ? '#f1f5f9' : 'white',
                                opacity: !revenueFilters.annee ? 0.55 : 1,
                                cursor: !revenueFilters.annee ? 'not-allowed' : 'pointer'
                            }}
                            value={revenueFilters.mois}
                            disabled={!revenueFilters.annee}
                            title={!revenueFilters.annee ? "Veuillez d'abord sélectionner une année" : "Sélectionnez un mois"}
                            onChange={e => {
                                if (!revenueFilters.annee) return;
                                setRevenueFilters({...revenueFilters, mois: e.target.value, dateDebut: '', dateFin: ''});
                            }}
                        >
                            <option value="">Tous les mois</option>
                            {['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'].map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                        <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>OU</span>
                        <input
                            type="date"
                            className="dbc-filter-input"
                            style={{ padding: '5px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', outline: 'none', background: 'white' }}
                            value={revenueFilters.dateDebut}
                            onChange={e => setRevenueFilters({...revenueFilters, dateDebut: e.target.value, mois: '', annee: ''})}
                            title="Date de début"
                        />
                        <input
                            type="date"
                            className="dbc-filter-input"
                            style={{ padding: '5px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', outline: 'none', background: 'white' }}
                            value={revenueFilters.dateFin}
                            onChange={e => setRevenueFilters({...revenueFilters, dateFin: e.target.value, mois: '', annee: ''})}
                            title="Date de fin"
                        />
                    </div>
                </div>
                <div className="dbc-stats-grid">
                    <div className="dbc-stat-card blue">
                        <div className="dbc-stat-card-header">
                            <div className="dbc-card-icon"><DollarSign size={24} /></div>
                            <div className="dbc-stat-card-title">
                                <span className="label">CA Total</span>
                                <span className="dbc-trend positive">Global</span>
                            </div>
                        </div>
                        <div className="dbc-card-info">
                            <span className="value">{formatCurrency(revenue.totalCA)}</span>
                            {revenue.details && revenue.details.ca && (
                                <div className="dbc-card-breakdown">
                                    <div className="dbc-breakdown-item">
                                        <span className="dbc-breakdown-label"><span className="dbc-dot auto"></span> Autofinancement</span>
                                        <strong>{formatCurrency(revenue.details.ca.comptant || 0)}</strong>
                                    </div>
                                    <div className="dbc-breakdown-item">
                                        <span className="dbc-breakdown-label"><span className="dbc-dot steg"></span> STEG</span>
                                        <strong>{formatCurrency(revenue.details.ca.credit || 0)}</strong>
                                    </div>
                                    <div className="dbc-breakdown-item" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                                        <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
                                            <span className="dbc-breakdown-label"><span className="dbc-dot mixte"></span> Mixte</span>
                                            <strong>{formatCurrency(revenue.details.ca.mixte || 0)}</strong>
                                        </div>
                                        {revenue.details.ca.mixteDetails && (
                                            <div style={{ display: 'flex', width: '100%', justifyContent: 'flex-end', fontSize: '0.75rem', marginTop: '2px', color: '#94a3b8' }}>
                                                <span>Auto: {formatCurrency(revenue.details.ca.mixteDetails.comptant || 0)} | STEG: {formatCurrency(revenue.details.ca.mixteDetails.credit || 0)}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            {revenue.details && revenue.details.ca && (
                                <div className="dbc-card-breakdown" style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #f1f5f9' }}>
                                    <div className="dbc-breakdown-item">
                                        <span className="dbc-breakdown-label" style={{ fontWeight: 600, color: '#475569' }}>Basse Tension (BT)</span>
                                        <strong>{formatCurrency(revenue.details.ca.bt || 0)}</strong>
                                    </div>
                                    <div className="dbc-breakdown-item">
                                        <span className="dbc-breakdown-label" style={{ fontWeight: 600, color: '#475569' }}>Moyenne Tension (MT)</span>
                                        <strong>{formatCurrency(revenue.details.ca.mt || 0)}</strong>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="dbc-stat-card green">
                        <div className="dbc-stat-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div className="dbc-card-icon"><CheckCircle size={24} /></div>
                                <div className="dbc-stat-card-title">
                                    <span className="label">Encaissé</span>
                                    <span className="dbc-trend positive">
                                        {Math.round((revenue.encaisse / revenue.totalCA) * 100)}%
                                    </span>
                                </div>
                            </div>
                            <button
                                type="button"
                                className="dbc-action-btn-sm"
                                onClick={() => onNavigate ? onNavigate('dashboard-clients', { tab: 'paiements' }) : navigate('/dashboard-clients?tab=paiements')}
                                title="Voir l'historique complet des paiements effectués"
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '5px',
                                    padding: '4px 10px',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    color: '#047857',
                                    backgroundColor: '#ecfdf5',
                                    border: '1px solid #a7f3d0',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                <span>Paiements effectués</span>
                                <ChevronRight size={14} />
                            </button>
                        </div>
                        <div className="dbc-card-info">
                            <span className="value">{formatCurrency(revenue.encaisse)}</span>
                            {revenue.details && revenue.details.encaisse && (
                                <div className="dbc-card-breakdown">
                                    <div className="dbc-breakdown-item">
                                        <span className="dbc-breakdown-label"><span className="dbc-dot auto"></span> Autofinancement</span>
                                        <strong>{formatCurrency(revenue.details.encaisse.comptant || 0)}</strong>
                                    </div>
                                    <div className="dbc-breakdown-item">
                                        <span className="dbc-breakdown-label"><span className="dbc-dot steg"></span> STEG</span>
                                        <strong>{formatCurrency(revenue.details.encaisse.credit || 0)}</strong>
                                    </div>
                                    <div className="dbc-breakdown-item" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                                        <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
                                            <span className="dbc-breakdown-label"><span className="dbc-dot mixte"></span> Mixte</span>
                                            <strong>{formatCurrency(revenue.details.encaisse.mixte || 0)}</strong>
                                        </div>
                                        {revenue.details.encaisse.mixteDetails && (
                                            <div style={{ display: 'flex', width: '100%', justifyContent: 'flex-end', fontSize: '0.75rem', marginTop: '2px', color: '#94a3b8' }}>
                                                <span>Auto: {formatCurrency(revenue.details.encaisse.mixteDetails.comptant || 0)} | STEG: {formatCurrency(revenue.details.encaisse.mixteDetails.credit || 0)}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            {revenue.details && revenue.details.encaisse && (
                                <div className="dbc-card-breakdown" style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #f1f5f9' }}>
                                    <div className="dbc-breakdown-item">
                                        <span className="dbc-breakdown-label" style={{ fontWeight: 600, color: '#475569' }}>Basse Tension (BT)</span>
                                        <strong>{formatCurrency(revenue.details.encaisse.bt || 0)}</strong>
                                    </div>
                                    <div className="dbc-breakdown-item">
                                        <span className="dbc-breakdown-label" style={{ fontWeight: 600, color: '#475569' }}>Moyenne Tension (MT)</span>
                                        <strong>{formatCurrency(revenue.details.encaisse.mt || 0)}</strong>
                                    </div>
                                </div>
                            )}
                            {revenue.details && revenue.details.encaisse && revenue.details.encaisse.parAnneeDossier && Object.keys(revenue.details.encaisse.parAnneeDossier).length > 0 && (
                                <div className="dbc-card-breakdown" style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px dashed #10b981' }}>
                                    <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#047857', marginBottom: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span>Par Année d'Arrivée Dossier :</span>
                                    </div>
                                    {Object.entries(revenue.details.encaisse.parAnneeDossier)
                                        .sort(([a], [b]) => b.localeCompare(a))
                                        .map(([annee, montant]) => (
                                            <div key={annee} className="dbc-breakdown-item" style={{ paddingLeft: '4px' }}>
                                                <span className="dbc-breakdown-label" style={{ fontSize: '0.78rem', color: '#334155' }}>
                                                    Clients / Dossiers {annee}
                                                </span>
                                                <strong style={{ fontSize: '0.8rem', color: '#047857' }}>
                                                    {formatCurrency(montant)}
                                                </strong>
                                            </div>
                                        ))
                                    }
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="dbc-stat-card yellow">
                        <div className="dbc-stat-card-header">
                            <div className="dbc-card-icon"><Clock size={24} /></div>
                            <div className="dbc-stat-card-title">
                                <span className="label">En Attente</span>
                                <span className="dbc-trend">
                                    {Math.round((revenue.enAttente / revenue.totalCA) * 100)}%
                                </span>
                            </div>
                        </div>
                        <div className="dbc-card-info">
                            <span className="value">{formatCurrency(revenue.enAttente)}</span>
                            {revenue.details && revenue.details.attente && (
                                <div className="dbc-card-breakdown">
                                    <div className="dbc-breakdown-item">
                                        <span className="dbc-breakdown-label"><span className="dbc-dot auto"></span> Autofinancement</span>
                                        <strong>{formatCurrency(revenue.details.attente.comptant || 0)}</strong>
                                    </div>
                                    <div className="dbc-breakdown-item">
                                        <span className="dbc-breakdown-label"><span className="dbc-dot steg"></span> STEG</span>
                                        <strong>{formatCurrency(revenue.details.attente.credit || 0)}</strong>
                                    </div>
                                    <div className="dbc-breakdown-item" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                                        <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
                                            <span className="dbc-breakdown-label"><span className="dbc-dot mixte"></span> Mixte</span>
                                            <strong>{formatCurrency(revenue.details.attente.mixte || 0)}</strong>
                                        </div>
                                        {revenue.details.attente.mixteDetails && (
                                            <div style={{ display: 'flex', width: '100%', justifyContent: 'flex-end', fontSize: '0.75rem', marginTop: '2px', color: '#94a3b8' }}>
                                                <span>Auto: {formatCurrency(revenue.details.attente.mixteDetails.comptant || 0)} | STEG: {formatCurrency(revenue.details.attente.mixteDetails.credit || 0)}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            {revenue.details && revenue.details.attente && (
                                <div className="dbc-card-breakdown" style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #f1f5f9' }}>
                                    <div className="dbc-breakdown-item">
                                        <span className="dbc-breakdown-label" style={{ fontWeight: 600, color: '#475569' }}>Basse Tension (BT)</span>
                                        <strong>{formatCurrency(revenue.details.attente.bt || 0)}</strong>
                                    </div>
                                    <div className="dbc-breakdown-item">
                                        <span className="dbc-breakdown-label" style={{ fontWeight: 600, color: '#475569' }}>Moyenne Tension (MT)</span>
                                        <strong>{formatCurrency(revenue.details.attente.mt || 0)}</strong>
                                    </div>
                                </div>
                            )}
                            {revenue.details && revenue.details.attenteMiseEnService && (
                                <div className="dbc-card-breakdown" style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px dashed rgba(217, 119, 6, 0.2)' }}>
                                    <div className="dbc-breakdown-item" style={{ color: '#d97706', marginBottom: '4px' }}>
                                        <strong>Déjà en service ({revenue.details.attenteMiseEnService.count} doss.)</strong>
                                        <strong>{formatCurrency(revenue.details.attenteMiseEnService.total)}</strong>
                                    </div>
                                    <div className="dbc-breakdown-item">
                                        <span className="dbc-breakdown-label"><span className="dbc-dot auto"></span> Autofinancement</span>
                                        <strong>{formatCurrency(revenue.details.attenteMiseEnService.details.comptant || 0)}</strong>
                                    </div>
                                    <div className="dbc-breakdown-item">
                                        <span className="dbc-breakdown-label"><span className="dbc-dot steg"></span> STEG</span>
                                        <strong>{formatCurrency(revenue.details.attenteMiseEnService.details.credit || 0)}</strong>
                                    </div>
                                    <div className="dbc-breakdown-item" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                                        <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
                                            <span className="dbc-breakdown-label"><span className="dbc-dot mixte"></span> Mixte</span>
                                            <strong>{formatCurrency(revenue.details.attenteMiseEnService.details.mixte || 0)}</strong>
                                        </div>
                                        {revenue.details.attenteMiseEnService.details.mixteDetails && (
                                            <div style={{ display: 'flex', width: '100%', justifyContent: 'flex-end', fontSize: '0.75rem', marginTop: '2px', color: '#94a3b8' }}>
                                                <span>Auto: {formatCurrency(revenue.details.attenteMiseEnService.details.mixteDetails.comptant || 0)} | STEG: {formatCurrency(revenue.details.attenteMiseEnService.details.mixteDetails.credit || 0)}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="dbc-stat-card red">
                        <div className="dbc-stat-card-header">
                            <div className="dbc-card-icon"><AlertTriangle size={24} /></div>
                            <div className="dbc-stat-card-title">
                                <span className="label">En Retard</span>
                                <span className="dbc-trend negative">Action requise</span>
                            </div>
                        </div>
                        <div className="dbc-card-info">
                            <span className="value">{formatCurrency(revenue.enRetard)}</span>
                            {revenue.details && revenue.details.retard && (
                                <div className="dbc-card-breakdown">
                                    <div className="dbc-breakdown-item">
                                        <span className="dbc-breakdown-label"><span className="dbc-dot auto"></span> Autofinancement</span>
                                        <strong>{formatCurrency(revenue.details.retard.comptant || 0)}</strong>
                                    </div>
                                    <div className="dbc-breakdown-item">
                                        <span className="dbc-breakdown-label"><span className="dbc-dot steg"></span> STEG</span>
                                        <strong>{formatCurrency(revenue.details.retard.credit || 0)}</strong>
                                    </div>
                                    <div className="dbc-breakdown-item" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                                        <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
                                            <span className="dbc-breakdown-label"><span className="dbc-dot mixte"></span> Mixte</span>
                                            <strong>{formatCurrency(revenue.details.retard.mixte || 0)}</strong>
                                        </div>
                                        {revenue.details.retard.mixteDetails && (
                                            <div style={{ display: 'flex', width: '100%', justifyContent: 'flex-end', fontSize: '0.75rem', marginTop: '2px', color: '#94a3b8' }}>
                                                <span>Auto: {formatCurrency(revenue.details.retard.mixteDetails.comptant || 0)} | STEG: {formatCurrency(revenue.details.retard.mixteDetails.credit || 0)}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            {revenue.details && revenue.details.retard && (
                                <div className="dbc-card-breakdown" style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #f1f5f9' }}>
                                    <div className="dbc-breakdown-item">
                                        <span className="dbc-breakdown-label" style={{ fontWeight: 600, color: '#475569' }}>Basse Tension (BT)</span>
                                        <strong>{formatCurrency(revenue.details.retard.bt || 0)}</strong>
                                    </div>
                                    <div className="dbc-breakdown-item">
                                        <span className="dbc-breakdown-label" style={{ fontWeight: 600, color: '#475569' }}>Moyenne Tension (MT)</span>
                                        <strong>{formatCurrency(revenue.details.retard.mt || 0)}</strong>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                </ProtectedFinancialCard>

                <ProtectedFinancialCard title="Répartition par méthode de paiement protégée" lockedClassName="dbc-payment-method-section">
                <section className="dbc-payment-method-section">
                    <div className="dbc-payment-method-header">
                        <div>
                            <h3>Répartition des montants par méthode de paiement</h3>
                            <span>Période sélectionnée</span>
                        </div>
                        <div className="dbc-payment-method-total">
                            <span>Total encaisse</span>
                            <strong>{formatCurrency(repartitionMethodesPaiement?.totalEncaisse || 0)}</strong>
                        </div>
                    </div>

                    <div className="dbc-payment-method-grid">
                        <div className="dbc-payment-method-panel encaisse">
                            <div className="dbc-payment-method-panel-title">
                                <CheckCircle size={20} />
                                <div>
                                    <h4>Montants encaissés / effectués</h4>
                                    <span>{methodesEncaissees.reduce((sum, item) => sum + (item.count || 0), 0)} paiement(s)</span>
                                </div>
                            </div>
                            {renderPaymentMethodRows(methodesEncaissees, 'Aucun paiement encaissé sur cette période.')}
                        </div>

                        <div className="dbc-payment-method-panel avenir">
                            <div className="dbc-payment-method-panel-title">
                                <Clock size={20} />
                                <div>
                                    <h4>Montants à venir</h4>
                                    <span>{formatCurrency(repartitionMethodesPaiement?.totalAVenir || 0)} restant</span>
                                </div>
                            </div>
                            {renderPaymentMethodRows(methodesAVenir, 'Aucun montant à venir sur cette période.', true)}
                        </div>
                    </div>
                </section>
                </ProtectedFinancialCard>

                <div className="dbc-charts-grid">
                    <ProtectedFinancialCard title="Revenus Mensuels (Encaissés) protégés" lockedClassName="dbc-chart-card">
                    <div className="dbc-chart-card">
                        <h3>Revenus Mensuels (Encaissés)</h3>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={revenue.monthly}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="month" />
                                <YAxis tickFormatter={(val) => `${val/1000}k`} />
                                <Tooltip formatter={(val) => formatCurrency(val)} />
                                <Bar dataKey="amount" fill={COLORS.primaryBlue} radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    </ProtectedFinancialCard>

                    <ProtectedFinancialCard title="Répartition des Paiements protégée" lockedClassName="dbc-chart-card">
                    <div className="dbc-chart-card">
                        <h3>Répartition des Paiements</h3>
                        <div className="dbc-split-charts">
                            <div className="dbc-pie-container">
                                <h4>Type Financement</h4>
                                <ResponsiveContainer width="100%" height={180}>
                                    <PieChart>
                                        <Pie data={revenue.repartition} innerRadius={50} outerRadius={70} paddingAngle={5} dataKey="value">
                                            {revenue.repartition.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                                <div className="dbc-custom-legend">
                                    {revenue.repartition.map((entry, index) => (
                                        <div key={index} className="dbc-legend-item">
                                            <span className="dbc-dot" style={{backgroundColor: CHART_COLORS[index % CHART_COLORS.length]}}></span>
                                            {entry.name}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="dbc-pie-container">
                                <h4>Statut Global</h4>
                                <ResponsiveContainer width="100%" height={180}>
                                    <PieChart>
                                        <Pie data={revenue.statutPaiements} innerRadius={50} outerRadius={70} paddingAngle={5} dataKey="value">
                                            <Cell fill={COLORS.green} />
                                            <Cell fill={COLORS.primaryYellow} />
                                            <Cell fill={COLORS.red} />
                                        </Pie>
                                        <Tooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                                <div className="dbc-custom-legend">
                                    <div className="dbc-legend-item"><span className="dbc-dot" style={{backgroundColor: COLORS.green}}></span> Payé</div>
                                    <div className="dbc-legend-item"><span className="dbc-dot" style={{backgroundColor: COLORS.primaryYellow}}></span> Attente</div>
                                    <div className="dbc-legend-item"><span className="dbc-dot" style={{backgroundColor: COLORS.red}}></span> Retard</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    </ProtectedFinancialCard>
                </div>

                <h2 className="dbc-section-title">Dépenses Dossiers (DF)</h2>
                <div className="dbc-filter-tabs">
                    <button className={`dbc-filter-btn ${activeDfPeriod === 'today' ? 'active' : ''}`} onClick={() => setActiveDfPeriod('today')}>Aujourd'hui</button>
                    <button className={`dbc-filter-btn ${activeDfPeriod === 'week' ? 'active' : ''}`} onClick={() => setActiveDfPeriod('week')}>Cette Semaine</button>
                    <button className={`dbc-filter-btn ${activeDfPeriod === 'month' ? 'active' : ''}`} onClick={() => setActiveDfPeriod('month')}>Ce Mois</button>
                    <button className={`dbc-filter-btn ${activeDfPeriod === 'year' ? 'active' : ''}`} onClick={() => setActiveDfPeriod('year')}>Cette Année</button>
                    <button className={`dbc-filter-btn ${activeDfPeriod === 'total' ? 'active' : ''}`} onClick={() => setActiveDfPeriod('total')}>Total</button>
                </div>

                <div className="dbc-stats-grid">
                    <div className="dbc-stat-card purple">
                        <div className="dbc-card-icon"><DollarSign size={24} /></div>
                        <div className="dbc-card-info">
                            <span className="label">Df Payé ({activeDfPeriod === 'total' ? 'Global' : activeDfPeriod})</span>
                            <span className="value">{formatCurrency(dfStats?.[activeDfPeriod]?.paid || 0)}</span>
                            <span className="dbc-trend positive">Total encaissé</span>
                        </div>
                    </div>
                    <div className="dbc-stat-card blue">
                        <div className="dbc-card-icon"><CheckCircle size={24} /></div>
                        <div className="dbc-card-info">
                            <span className="label">Df Part STEG</span>
                            <span className="value">{formatCurrency(dfStats?.[activeDfPeriod]?.steg || 0)}</span>
                            <span className="dbc-trend">107.100 DT / dossier</span>
                        </div>
                    </div>
                    <div className="dbc-stat-card orange">
                        <div className="dbc-card-icon"><Users size={24} /></div>
                        <div className="dbc-card-info">
                            <span className="label">Df Part Comptant</span>
                            <span className="value">{formatCurrency(dfStats?.[activeDfPeriod]?.comptant || 0)}</span>
                            <span className="dbc-trend">59.500 DT / dossier</span>
                        </div>
                    </div>
                    <div className="dbc-stat-card red">
                        <div className="dbc-card-icon"><Trash2 size={24} /></div>
                        <div className="dbc-card-info">
                            <span className="label">Df Payé (Supprimé)</span>
                            <span className="value">{formatCurrency(dfStats?.[activeDfPeriod]?.supprimes || 0)}</span>
                            <span className="dbc-trend negative">Frais perdus</span>
                        </div>
                    </div>
                </div>

                <h2 className="dbc-section-title">Suivi des Clients</h2>
                <div className="dbc-stats-grid">
                    <div className="dbc-stat-card white">
                        <div className="dbc-card-icon dbc-icon-blue"><Users size={24} /></div>
                        <div className="dbc-card-info">
                            <span className="label">Total Clients</span>
                            <span className="value">{clients.total}</span>
                        </div>
                    </div>
                    <div className="dbc-stat-card white">
                        <div className="dbc-card-icon dbc-icon-green"><TrendingUp size={24} /></div>
                        <div className="dbc-card-info">
                            <span className="label">Nouveaux</span>
                            <span className="value">{clients.nouveauxCeMois}</span>
                        </div>
                    </div>
                    <div className="dbc-stat-card white">
                        <div className="dbc-card-icon dbc-icon-yellow"><CheckCircle size={24} /></div>
                        <div className="dbc-card-info">
                            <span className="label">Actifs</span>
                            <span className="value">{clients.actifs}</span>
                        </div>
                    </div>
                    <div className="dbc-stat-card white">
                        <div className="dbc-card-icon dbc-icon-red"><AlertTriangle size={24} /></div>
                        <div className="dbc-card-info">
                            <span className="label">En Retard</span>
                            <span className="value">{clients.enRetard}</span>
                        </div>
                    </div>
                </div>

                <div className="dbc-charts-grid">
                    <div className="dbc-chart-card">
                        <h3>Résumé Échéances</h3>
                        <div className="dbc-deadlines-grid">
                            <div className="dbc-deadline-item">
                                <span className="dbc-dl-label">Prochaine Échéance</span>
                                <span className="dbc-dl-value">
                                    {echeances.prochaineGlobale
                                        ? new Date(echeances.prochaineGlobale.dateEcheance).toLocaleDateString('fr-FR')
                                        : 'Aucune'}
                                </span>
                            </div>
                            <div className="dbc-deadline-item">
                                <span className="dbc-dl-label">Montant Global</span>
                                <span className="dbc-dl-value dbc-dl-highlight">{formatCurrency(echeances.montantProchaine)}</span>
                            </div>
                            <div className="dbc-deadline-item">
                                <span className="dbc-dl-label">Ce mois</span>
                                <span className="dbc-dl-value">{echeances.ceMois}</span>
                            </div>
                            <div className="dbc-deadline-item">
                                <span className="dbc-dl-label">En Retard</span>
                                <span className="dbc-dl-value dbc-text-red">{echeances.enRetardCount}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section 3: Gestion des Dossiers et Paiements (TABBED) */}
                <div className="dbc-management-section">
                    <div className="dbc-management-nav">
                        <div
                            className={`dbc-nav-item ${activeTab === 'retards' ? 'active' : ''}`}
                            onClick={() => setActiveTab('retards')}
                        >
                            Retards Paiement ({retardsList.length})
                        </div>
                        <div
                            className={`dbc-nav-item ${activeTab === 'bloques' ? 'active' : ''}`}
                            onClick={() => setActiveTab('bloques')}
                        >
                            Bloqués Sastra ({sastraBloquesList.length})
                        </div>
                        <div
                            className={`dbc-nav-item ${activeTab === 'echeances' ? 'active' : ''}`}
                            onClick={() => setActiveTab('echeances')}
                        >
                            Échéances à venir ({echeancesAVenirList.length})
                            <span style={{ marginLeft: 8, fontSize: '0.75rem', color: '#6b7280', fontWeight: 500 }}>
                                Filtré: {filterStartLabel} - {filterEndLabel}
                            </span>
                        </div>
                        <div
                            className={`dbc-nav-item ${activeTab === 'supprimes' ? 'active' : ''}`}
                            onClick={() => setActiveTab('supprimes')}
                        >
                            Paiements Supprimés ({paiementsSupprimesList.length})
                        </div>
                        <div
                            className={`dbc-nav-item ${activeTab === 'clientsSupprimes' ? 'active' : ''}`}
                            onClick={() => setActiveTab('clientsSupprimes')}
                        >
                            Clients Supprimés ({clientsSupprimesList.length})
                        </div>
                        <div
                            className={`dbc-nav-item ${activeTab === 'dossiersSupprimes' ? 'active' : ''}`}
                            onClick={() => setActiveTab('dossiersSupprimes')}
                        >
                            Dossiers Supprimés / Annulés ({dossiersSupprimesList.length})
                        </div>
                        <div
                            className={`dbc-nav-item ${activeTab === 'extensions' ? 'active' : ''}`}
                            onClick={() => setActiveTab('extensions')}
                        >
                            Extensions En Attente ({extensionsList.length})
                        </div>
                    </div>

                    <div className="dbc-table-controls">
                        <div className="dbc-search-wrapper">
                            <Search className="dbc-search-icon" size={18} />
                            <input
                                type="text"
                                className="dbc-search-input"
                                placeholder="Rechercher par client, référence..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    <div style={{ overflowX: 'auto' }}>
                        {activeTab === 'retards' && (
                            <table className="dbc-modern-table">
                                <thead>
                                    <tr>
                                        <th>Client</th>
                                        <th>Réf STEG</th>
                                        <th>Échéance</th>
                                        <th>Mise en service</th>
                                        <th>Montant</th>
                                        <th>Retard</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {getPaginatedData(retardsList, currentPageByTab.retards).data.map((ech, idx) => {
                                        const diffDays = Math.ceil(Math.abs(new Date() - new Date(ech.dateEcheance)) / (1000 * 60 * 60 * 24));
                                        return (
                                            <tr key={idx}>
                                                <td className="dbc-client-name">{ech.clientName}</td>
                                                <td><code>{ech.referenceSteg}</code></td>
                                                <td>{new Date(ech.dateEcheance).toLocaleDateString('fr-FR')}</td>
                                                <td>{ech.dateMiseEnService ? new Date(ech.dateMiseEnService).toLocaleDateString('fr-FR') : '-'}</td>
                                                <td className="dbc-montant dbc-text-red">{formatCurrency(ech.montant)}</td>
                                                <td><span className="dbc-badge retard">+{diffDays} j</span></td>
                                                <td>
                                                    <button className="dbc-view-btn" onClick={() => onNavigate(`client-detail`, { clientId: ech.clientId })}>
                                                        Voir Dossier
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}

                        {activeTab === 'bloques' && (
                            <table className="dbc-modern-table">
                                <thead>
                                    <tr>
                                        <th>Client</th>
                                        <th>Réf STEG</th>
                                        <th>État Dossier</th>
                                        <th>Dépôt</th>
                                        <th>Attente</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {getPaginatedData(sastraBloquesList, currentPageByTab.bloques).data.map((dossier, idx) => (
                                        <tr key={idx}>
                                            <td className="dbc-client-name">{dossier.clientName}</td>
                                            <td><code>{dossier.referenceSteg}</code></td>
                                            <td><span className="dbc-badge" style={{ backgroundColor: '#fef3c7', color: '#b45309' }}>{dossier.etatDossier || 'Déposé chez Sastra'}</span></td>
                                            <td>{new Date(dossier.dateCreation).toLocaleDateString('fr-FR')}</td>
                                            <td><span className="dbc-badge" style={{ backgroundColor: '#ffedd5', color: '#ea580c' }}>{dossier.daysCount} j</span></td>
                                            <td>
                                                <button className="dbc-view-btn" onClick={() => onNavigate(`client-detail`, { clientId: dossier.clientId })}>
                                                    Voir Dossier
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}

                        {activeTab === 'echeances' && (
                            <table className="dbc-modern-table">
                                <thead>
                                    <tr>
                                        <th>Client</th>
                                        <th>Réf STEG</th>
                                        <th>Date</th>
                                        <th>Montant</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {getPaginatedData(echeancesAVenirList, currentPageByTab.echeances).data.map((ech, idx) => (
                                        <tr key={idx}>
                                            <td className="dbc-client-name">{ech.clientName}</td>
                                            <td><code>{ech.referenceSteg}</code></td>
                                            <td>{new Date(ech.dateEcheance).toLocaleDateString('fr-FR')}</td>
                                            <td className="dbc-montant">{formatCurrency(ech.montant)}</td>
                                            <td>
                                                <button className="dbc-view-btn" onClick={() => onNavigate(`client-detail`, {clientId: ech.clientId})}>
                                                    Voir
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}

                        {activeTab === 'supprimes' && (
                            <table className="dbc-modern-table">
                                <thead>
                                    <tr>
                                        <th>Montant</th>
                                        <th>Client</th>
                                        <th>Réf</th>
                                        <th>Par</th>
                                        <th>Le</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {getPaginatedData(paiementsSupprimesList, currentPageByTab.supprimes).data.map((p, idx) => (
                                        <tr key={idx}>
                                            <td className="dbc-montant" style={{color: '#6b7280'}}>{formatCurrency(p.montant)}</td>
                                            <td className="dbc-client-name">{p.clientName}</td>
                                            <td><code>{p.referenceSteg}</code></td>
                                            <td style={{fontSize: '0.8rem'}}>{p.supprimePar}</td>
                                            <td>{new Date(p.dateSuppression).toLocaleDateString('fr-FR')}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}

                        {activeTab === 'clientsSupprimes' && (
                            <div className="dbc-deleted-list-card">
                                <div className="dbc-deleted-list-header">
                                    <h3><Trash2 size={18} /> Clients supprimés</h3>
                                    <span>{activePagination.totalItems}</span>
                                </div>
                                <table className="dbc-modern-table">
                                    <thead>
                                        <tr>
                                            <th>Client</th>
                                            <th>CIN</th>
                                            <th>Dossiers</th>
                                            <th>Par</th>
                                            <th>Le</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {activePagination.data.length > 0 ? (
                                            activePagination.data.map((client, idx) => (
                                                <tr key={client.clientId || idx}>
                                                    <td className="dbc-client-name">{client.clientName || 'Client sans nom'}</td>
                                                    <td><code>{client.cin || '-'}</code></td>
                                                    <td><span className="dbc-badge deleted">{client.nbDossiers || 0}</span></td>
                                                    <td style={{fontSize: '0.8rem'}}>{client.supprimePar || 'N/A'}</td>
                                                    <td>{client.dateSuppression ? new Date(client.dateSuppression).toLocaleDateString('fr-FR') : '-'}</td>
                                                    <td>
                                                        {client.prospectId ? (
                                                            <button className="dbc-view-btn" onClick={() => goToProspectDetail(client.prospectId)}>
                                                                Voir prospect
                                                            </button>
                                                        ) : (
                                                            <span className="dbc-muted-text">Prospect introuvable</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr><td colSpan="6" className="dbc-empty-row">Aucun client supprimé</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {activeTab === 'dossiersSupprimes' && (
                            <div className="dbc-deleted-list-card">
                                <div className="dbc-deleted-list-header">
                                    <h3><Trash2 size={18} /> Dossiers supprimés / annulés</h3>
                                    <span>{activePagination.totalItems}</span>
                                </div>
                                <table className="dbc-modern-table">
                                    <thead>
                                        <tr>
                                            <th>Client</th>
                                            <th>Réf</th>
                                            <th>Montant</th>
                                            <th>DF</th>
                                            <th>État</th>
                                            <th>Par</th>
                                            <th>Le</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {activePagination.data.length > 0 ? (
                                            activePagination.data.map((dossier, idx) => (
                                                <tr key={dossier.dossierId || idx}>
                                                    <td className="dbc-client-name">{dossier.clientName || 'Client sans nom'}</td>
                                                    <td><code>{dossier.referenceSteg || '-'}</code></td>
                                                    <td className="dbc-montant">{formatCurrency(dossier.montant || 0)}</td>
                                                    <td className="dbc-montant" style={{color: '#dc2626'}}>{formatCurrency(dossier.montantDF || 0)}</td>
                                                    <td>
                                                        <span className={`dbc-badge ${dossier.isCanceled ? 'canceled' : 'deleted'}`}>
                                                            {dossier.statutSuppression || (dossier.isCanceled ? 'Annulé' : 'Supprimé')}
                                                        </span>
                                                    </td>
                                                    <td style={{fontSize: '0.8rem'}}>{dossier.supprimePar || 'N/A'}</td>
                                                    <td>{dossier.dateSuppression ? new Date(dossier.dateSuppression).toLocaleDateString('fr-FR') : '-'}</td>
                                                    <td>
                                                        {dossier.prospectId ? (
                                                            <button className="dbc-view-btn" onClick={() => goToProspectDetail(dossier.prospectId)}>
                                                                Voir prospect
                                                            </button>
                                                        ) : (
                                                            <span className="dbc-muted-text">Prospect introuvable</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr><td colSpan="8" className="dbc-empty-row">Aucun dossier supprimé ou annulé</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {activeTab === 'extensions' && (
                            <table className="dbc-modern-table">
                                <thead>
                                    <tr>
                                        <th>Client</th>
                                        <th>Réf STEG</th>
                                        <th>Panneaux</th>
                                        <th>Puissance</th>
                                        <th>Coût</th>
                                        <th>Date</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {getPaginatedData(extensionsList, currentPageByTab.extensions).data.map((ext, idx) => (
                                        <tr key={idx}>
                                            <td className="dbc-client-name">{ext.clientName}</td>
                                            <td><code>{ext.referenceSteg}</code></td>
                                            <td>{ext.nbrPanneaux}</td>
                                            <td style={{fontWeight: '600', color: '#2563eb'}}>+{ext.puissanceAjoutee} kWc</td>
                                            <td className="dbc-montant">{formatCurrency(ext.coutExtension)}</td>
                                            <td>{new Date(ext.dateCreation).toLocaleDateString('fr-FR')}</td>
                                            <td>
                                                <button className="dbc-view-btn" onClick={() => onNavigate(`client-detail`, { clientId: ext.clientId })}>
                                                    Valider / Gérer
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* Pagination */}
                    {activePagination.totalPages > 1 && (
                        <div className="dbc-pagination">
                            <button
                                className="dbc-page-btn"
                                disabled={activePage === 1}
                                onClick={() => setCurrentPageByTab(prev => ({
                                    ...prev,
                                    [activeTab]: Math.max(1, activePage - 1)
                                }))}
                            >
                                <ChevronLeft size={16} />
                            </button>
                            {[...Array(activePagination.totalPages)].map((_, i) => (
                                <button
                                    key={i}
                                    className={`dbc-page-btn ${activePage === i + 1 ? 'active' : ''}`}
                                    onClick={() => setCurrentPageByTab(prev => ({
                                        ...prev,
                                        [activeTab]: i + 1
                                    }))}
                                >
                                    {i + 1}
                                </button>
                            ))}
                            <button
                                className="dbc-page-btn"
                                disabled={activePage === activePagination.totalPages}
                                onClick={() => setCurrentPageByTab(prev => ({
                                    ...prev,
                                    [activeTab]: Math.min(activePagination.totalPages, activePage + 1)
                                }))}
                            >
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DashboardClient;
